import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../contexts/auth/Context";
import { useUserProfile } from "../useUserProfile";
import { uploadToS3Fn } from "./s3-upload";
import { updateUserProfileRequest } from "../../requests/user/profile";
import { deleteS3ObjectRequest } from "../../requests/aws/s3";
import { USER_PROFILE_QUERY_KEY } from "../../../query/keys";
import { compressImageForUpload } from "../../../utils/imageCompression";

export const useUploadUserAvatar = () => {
  const { token } = useAuth();
  const { userProfile } = useUserProfile();
  const queryClient = useQueryClient();
  const [isCompressing, setCompressing] = useState(false);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      if (!token) throw new Error("Sesión expirada o no válida");
      if (!userProfile?.publicId)
        throw new Error("No se encontró el ID del usuario");

      setCompressing(true);
      let compressed: File;
      try {
        compressed = await compressImageForUpload(file);
      } finally {
        setCompressing(false);
      }

      const uploadResult = await uploadToS3Fn(
        compressed,
        token,
        "users",
        userProfile.publicId,
        "logo",
      );

      if (!uploadResult.success || !uploadResult.key) {
        throw new Error(uploadResult.error || "Error al subir archivo");
      }

      await updateUserProfileRequest(
        { profilePictureKey: uploadResult.key },
        token,
      );

      if (
        userProfile.profilePictureKey &&
        userProfile.profilePictureKey !== uploadResult.key
      ) {
        await deleteS3ObjectRequest(userProfile.profilePictureKey, token);
      }

      return uploadResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
    },
  });

  return {
    uploadAvatar: mutation.mutateAsync,
    isCompressing,
    isUploading: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error.message : null,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};
